export type CuratedAppId =
  | 'ahrefs'
  | 'airtable'
  | 'apollo-io'
  | 'asana'
  | 'attio'
  | 'calendly'
  | 'clickup'
  | 'cloudflare'
  | 'datadog'
  | 'datadog-eu'
  | 'datadog-us5'
  | 'exa'
  | 'figma'
  | 'fireflies'
  | 'gamma'
  | 'gitlab'
  | 'gmail'
  | 'gong'
  | 'google-calendar'
  | 'google-drive'
  | 'honeycomb'
  | 'hubspot'
  | 'hugging-face'
  | 'jotform'
  | 'klaviyo'
  | 'linear'
  | 'lunarcrush'
  | 'mailerlite'
  | 'miro'
  | 'mixpanel'
  | 'monday-com'
  | 'netlify'
  | 'notion'
  | 'pagerduty'
  | 'posthog'
  | 'postman'
  | 'salesforce'
  | 'sentry'
  | 'snowflake'
  | 'square'
  | 'stripe'
  | 'supabase'
  | 'tavily'
  | 'todoist'
  | 'vercel'
  | 'webflow';

export type CuratedAppCredentialField = {
  key: string;
  label: string;
  secret: boolean;
  required?: boolean;
  placeholder?: string;
  help?: string;
};

export type CuratedAppHeader = {
  name: string;
  valueField: string;
  prefix?: string;
};

export type CuratedAppRoute = {
  host: string;
  configurable?: boolean;
  hostField?: string;
  allowedHostSuffixes?: string[];
  pathPrefixes?: string[];
};

export type CuratedAppDefinition = {
  id: CuratedAppId;
  name: string;
  domain: string;
  tool: 'cli' | 'mcp' | 'api';
  credentialType: 'headers' | 'basic' | 'oauth2_authorization_code' | 'oauth2_client_credentials';
  fields: CuratedAppCredentialField[];
  headers?: CuratedAppHeader[];
  routes: CuratedAppRoute[];
  scopes?: string[];
  testConnection?: boolean;
  plugin?: boolean;
  accent: string;
};

const secret = (
  key: string,
  label: string,
  placeholder?: string,
): CuratedAppCredentialField => ({
  key,
  label,
  secret: true,
  required: true,
  ...(placeholder === undefined ? {} : { placeholder }),
});

const text = (
  key: string,
  label: string,
  placeholder?: string,
  required = true,
): CuratedAppCredentialField => ({
  key,
  label,
  secret: false,
  required,
  ...(placeholder === undefined ? {} : { placeholder }),
});

const bearer = (valueField: string): CuratedAppHeader[] => [{
  name: 'Authorization',
  prefix: 'Bearer',
  valueField,
}];

const authorization = (valueField: string, prefix?: string): CuratedAppHeader[] => [{
  name: 'Authorization',
  ...(prefix ? { prefix } : {}),
  valueField,
}];

const tokenApp = (input: Omit<CuratedAppDefinition, 'credentialType' | 'fields' | 'headers'> & {
  fieldLabel: string;
  fieldKey?: string;
  header?: string;
  prefix?: string;
  placeholder?: string;
}): CuratedAppDefinition => {
  const fieldKey = input.fieldKey ?? 'token';
  const {
    fieldLabel,
    header = 'Authorization',
    prefix = 'Bearer',
    placeholder,
    ...definition
  } = input;
  return {
    ...definition,
    credentialType: 'headers',
    fields: [secret(fieldKey, fieldLabel, placeholder)],
    headers: [{ name: header, ...(prefix ? { prefix } : {}), valueField: fieldKey }],
  };
};

const googleScopes = {
  identity: ['openid', 'https://www.googleapis.com/auth/userinfo.email'],
  gmail: [
    'https://www.googleapis.com/auth/gmail.readonly',
    'https://www.googleapis.com/auth/gmail.compose',
    'https://www.googleapis.com/auth/gmail.modify',
  ],
  calendar: [
    'https://www.googleapis.com/auth/calendar.readonly',
    'https://www.googleapis.com/auth/calendar.events.readonly',
    'https://www.googleapis.com/auth/calendar',
  ],
  drive: [
    'https://www.googleapis.com/auth/drive.readonly',
    'https://www.googleapis.com/auth/drive.file',
  ],
} as const;

export const curatedApps: readonly CuratedAppDefinition[] = [
  tokenApp({ id: 'ahrefs', name: 'Ahrefs', domain: 'api.ahrefs.com', tool: 'mcp', fieldLabel: "Claude's API key", fieldKey: 'api_key', routes: [{ host: 'api.ahrefs.com' }], accent: '#ef4444' }),
  tokenApp({ id: 'airtable', name: 'Airtable', domain: 'api.airtable.com', tool: 'cli', fieldLabel: "Claude's personal access token", routes: [{ host: 'api.airtable.com' }], testConnection: true, accent: '#f59e0b' }),
  tokenApp({ id: 'apollo-io', name: 'Apollo.io', domain: 'api.apollo.io', tool: 'cli', fieldLabel: "Claude's API key", fieldKey: 'api_key', header: 'X-Api-Key', prefix: '', routes: [{ host: 'api.apollo.io' }], accent: '#6366f1' }),
  tokenApp({ id: 'asana', name: 'Asana', domain: 'app.asana.com', tool: 'mcp', fieldLabel: "Claude's personal access token", routes: [{ host: 'app.asana.com' }], testConnection: true, plugin: true, accent: '#f43f5e' }),
  tokenApp({ id: 'attio', name: 'Attio', domain: 'api.attio.com', tool: 'cli', fieldLabel: "Claude's API key", fieldKey: 'api_key', routes: [{ host: 'api.attio.com' }], testConnection: true, accent: '#64748b' }),
  tokenApp({ id: 'calendly', name: 'Calendly', domain: 'api.calendly.com', tool: 'mcp', fieldLabel: "Claude's personal access token", routes: [{ host: 'api.calendly.com' }], accent: '#2563eb' }),
  tokenApp({ id: 'clickup', name: 'ClickUp', domain: 'api.clickup.com', tool: 'mcp', fieldLabel: "Claude's personal API token", prefix: '', routes: [{ host: 'api.clickup.com' }], testConnection: true, accent: '#a855f7' }),
  tokenApp({ id: 'cloudflare', name: 'Cloudflare', domain: 'api.cloudflare.com', tool: 'cli', fieldLabel: "Claude's API token", routes: [{ host: 'api.cloudflare.com' }], accent: '#f97316' }),
  {
    id: 'datadog', name: 'Datadog', domain: 'api.datadoghq.com', tool: 'cli', credentialType: 'headers',
    fields: [secret('api_key', "Claude's API key"), secret('application_key', "Claude's application key")],
    headers: [{ name: 'DD-API-KEY', valueField: 'api_key' }, { name: 'DD-APPLICATION-KEY', valueField: 'application_key' }],
    routes: [{ host: 'api.datadoghq.com' }], testConnection: true, plugin: true, accent: '#632ca6',
  },
  {
    id: 'datadog-eu', name: 'Datadog (EU)', domain: 'api.datadoghq.eu', tool: 'cli', credentialType: 'headers',
    fields: [secret('api_key', "Claude's API key"), secret('application_key', "Claude's application key")],
    headers: [{ name: 'DD-API-KEY', valueField: 'api_key' }, { name: 'DD-APPLICATION-KEY', valueField: 'application_key' }],
    routes: [{ host: 'api.datadoghq.eu' }], testConnection: true, plugin: true, accent: '#632ca6',
  },
  {
    id: 'datadog-us5', name: 'Datadog (US5)', domain: 'api.us5.datadoghq.com', tool: 'cli', credentialType: 'headers',
    fields: [secret('api_key', "Claude's API key"), secret('application_key', "Claude's application key")],
    headers: [{ name: 'DD-API-KEY', valueField: 'api_key' }, { name: 'DD-APPLICATION-KEY', valueField: 'application_key' }],
    routes: [{ host: 'api.us5.datadoghq.com' }], testConnection: true, plugin: true, accent: '#632ca6',
  },
  tokenApp({ id: 'exa', name: 'Exa', domain: 'api.exa.ai', tool: 'mcp', fieldLabel: "Claude's API key", fieldKey: 'api_key', header: 'x-api-key', prefix: '', routes: [{ host: 'api.exa.ai' }], accent: '#5555ff' }),
  tokenApp({ id: 'figma', name: 'Figma', domain: 'api.figma.com', tool: 'cli', fieldLabel: "Claude's personal access token", header: 'X-Figma-Token', prefix: '', routes: [{ host: 'api.figma.com' }], accent: '#a259ff' }),
  tokenApp({ id: 'fireflies', name: 'Fireflies', domain: 'api.fireflies.ai', tool: 'mcp', fieldLabel: "Claude's API key", fieldKey: 'api_key', routes: [{ host: 'api.fireflies.ai' }], testConnection: true, accent: '#7c3aed' }),
  tokenApp({ id: 'gamma', name: 'Gamma', domain: 'public-api.gamma.app', tool: 'mcp', fieldLabel: "Claude's API key", fieldKey: 'api_key', header: 'X-API-KEY', prefix: '', routes: [{ host: 'public-api.gamma.app' }], accent: '#8b5cf6' }),
  tokenApp({ id: 'gitlab', name: 'GitLab', domain: 'gitlab.com', tool: 'cli', fieldLabel: "Claude's personal access token", routes: [{ host: 'gitlab.com', pathPrefixes: ['/api/'] }], accent: '#fc6d26' }),
  {
    id: 'gmail', name: 'Gmail', domain: '*.googleapis.com', tool: 'cli', credentialType: 'oauth2_authorization_code',
    fields: [secret('access_token', 'Google OAuth access token')], headers: bearer('access_token'), routes: [{ host: '*.googleapis.com', pathPrefixes: ['/gmail/', '/upload/gmail/', '/batch/gmail/'] }],
    scopes: [...googleScopes.identity, ...googleScopes.gmail], accent: '#ea4335',
  },
  {
    id: 'gong', name: 'Gong', domain: 'api.gong.io', tool: 'mcp', credentialType: 'basic',
    fields: [secret('username', "Claude's access key"), secret('password', "Claude's access key secret")],
    routes: [{ host: 'api.gong.io' }], testConnection: true, accent: '#7c3aed',
  },
  {
    id: 'google-calendar', name: 'Google Calendar', domain: '*.googleapis.com', tool: 'cli', credentialType: 'oauth2_authorization_code',
    fields: [secret('access_token', 'Google OAuth access token')], headers: bearer('access_token'), routes: [{ host: '*.googleapis.com', pathPrefixes: ['/calendar/', '/batch/calendar/'] }],
    scopes: [...googleScopes.identity, ...googleScopes.calendar], accent: '#4285f4',
  },
  {
    id: 'google-drive', name: 'Google Drive', domain: '*.googleapis.com', tool: 'cli', credentialType: 'oauth2_authorization_code',
    fields: [secret('access_token', 'Google OAuth access token')], headers: bearer('access_token'), routes: [{ host: '*.googleapis.com', pathPrefixes: ['/drive/', '/upload/drive/', '/batch/drive/'] }],
    scopes: [...googleScopes.identity, ...googleScopes.drive], accent: '#0f9d58',
  },
  tokenApp({ id: 'honeycomb', name: 'Honeycomb', domain: 'api.honeycomb.io', tool: 'cli', fieldLabel: "Claude's API key", fieldKey: 'api_key', header: 'X-Honeycomb-Team', prefix: '', routes: [{ host: 'api.honeycomb.io' }], accent: '#fbbf24' }),
  tokenApp({ id: 'hubspot', name: 'HubSpot', domain: 'api.hubapi.com', tool: 'cli', fieldLabel: "Claude's private app token", routes: [{ host: 'api.hubapi.com' }], testConnection: true, plugin: true, accent: '#ff7a59' }),
  tokenApp({ id: 'hugging-face', name: 'Hugging Face', domain: 'huggingface.co', tool: 'cli', fieldLabel: "Claude's user access token", routes: [{ host: 'huggingface.co', pathPrefixes: ['/api/'] }], accent: '#facc15' }),
  tokenApp({ id: 'jotform', name: 'Jotform', domain: 'api.jotform.com', tool: 'mcp', fieldLabel: "Claude's API key", fieldKey: 'api_key', header: 'APIKEY', prefix: '', routes: [{ host: 'api.jotform.com' }], accent: '#0a1551' }),
  tokenApp({ id: 'klaviyo', name: 'Klaviyo', domain: 'a.klaviyo.com', tool: 'cli', fieldLabel: "Claude's private API key", fieldKey: 'api_key', prefix: 'Klaviyo-API-Key', routes: [{ host: 'a.klaviyo.com' }], accent: '#111827' }),
  tokenApp({ id: 'linear', name: 'Linear', domain: 'api.linear.app', tool: 'cli', fieldLabel: "Claude's API key", fieldKey: 'api_key', prefix: '', routes: [{ host: 'api.linear.app' }], testConnection: true, plugin: true, accent: '#5e6ad2' }),
  tokenApp({ id: 'lunarcrush', name: 'LunarCrush', domain: 'lunarcrush.com', tool: 'cli', fieldLabel: "Claude's API key", fieldKey: 'api_key', routes: [{ host: 'lunarcrush.com', pathPrefixes: ['/api4/'] }], accent: '#0ea5e9' }),
  tokenApp({ id: 'mailerlite', name: 'MailerLite', domain: 'connect.mailerlite.com', tool: 'cli', fieldLabel: "Claude's API token", routes: [{ host: 'connect.mailerlite.com' }], accent: '#22c55e' }),
  tokenApp({ id: 'miro', name: 'Miro', domain: 'api.miro.com', tool: 'mcp', fieldLabel: "Claude's access token", routes: [{ host: 'api.miro.com' }], accent: '#ffd02f' }),
  {
    id: 'mixpanel', name: 'Mixpanel', domain: 'mixpanel.com', tool: 'mcp', credentialType: 'basic',
    fields: [text('username', "Claude's service account username"), secret('password', "Claude's service account secret")],
    routes: [{ host: 'mixpanel.com', pathPrefixes: ['/api/', '/query/', '/export/'] }], accent: '#7856ff',
  },
  tokenApp({ id: 'monday-com', name: 'monday.com', domain: 'api.monday.com', tool: 'cli', fieldLabel: "Claude's API token", prefix: '', routes: [{ host: 'api.monday.com' }], accent: '#f62b54' }),
  tokenApp({ id: 'netlify', name: 'Netlify', domain: 'api.netlify.com', tool: 'cli', fieldLabel: "Claude's personal access token", routes: [{ host: 'api.netlify.com' }], accent: '#00c7b7' }),
  tokenApp({ id: 'notion', name: 'Notion', domain: 'api.notion.com', tool: 'cli', fieldLabel: "Claude's integration secret", routes: [{ host: 'api.notion.com' }], plugin: true, accent: '#111111' }),
  tokenApp({ id: 'pagerduty', name: 'PagerDuty', domain: 'api.pagerduty.com', tool: 'cli', fieldLabel: "Claude's API key", fieldKey: 'api_key', prefix: 'Token token=', routes: [{ host: 'api.pagerduty.com' }], testConnection: true, plugin: true, accent: '#06ac38' }),
  tokenApp({ id: 'posthog', name: 'PostHog', domain: '*.posthog.com', tool: 'cli', fieldLabel: "Claude's personal API key", fieldKey: 'api_key', routes: [{ host: '*.posthog.com' }], testConnection: true, accent: '#f9bd2b' }),
  tokenApp({ id: 'postman', name: 'Postman', domain: 'api.getpostman.com', tool: 'cli', fieldLabel: "Claude's API key", fieldKey: 'api_key', header: 'X-Api-Key', prefix: '', routes: [{ host: 'api.getpostman.com' }], accent: '#ff6c37' }),
  {
    id: 'salesforce', name: 'Salesforce', domain: 'your-company.my.salesforce.com', tool: 'cli', credentialType: 'oauth2_client_credentials',
    fields: [
      text('host', 'Salesforce host', 'your-company.my.salesforce.com'),
      text('client_id', 'Client ID'),
      secret('client_secret', 'Client secret'),
      text('token_url', 'Token URL', 'https://login.salesforce.com/services/oauth2/token'),
      text('scope', 'Scopes (optional)', '', false),
      text('resource', 'Resource (optional)', '', false),
      text('audience', 'Audience (optional)', '', false),
    ],
    headers: bearer('access_token'), routes: [{ host: 'your-company.my.salesforce.com', configurable: true, hostField: 'host', allowedHostSuffixes: ['.my.salesforce.com'] }], accent: '#00a1e0',
  },
  tokenApp({ id: 'sentry', name: 'Sentry', domain: 'sentry.io', tool: 'cli', fieldLabel: "Claude's auth token", routes: [{ host: 'sentry.io', pathPrefixes: ['/api/'] }], testConnection: true, plugin: true, accent: '#362d59' }),
  {
    id: 'snowflake', name: 'Snowflake', domain: 'account.snowflakecomputing.com', tool: 'cli', credentialType: 'headers',
    fields: [text('host', 'Snowflake account host', 'org-account.snowflakecomputing.com'), secret('token', "Claude's programmatic access token")],
    headers: authorization('token', 'Bearer'), routes: [{ host: 'account.snowflakecomputing.com', configurable: true, hostField: 'host', allowedHostSuffixes: ['.snowflakecomputing.com'] }], accent: '#29b5e8',
  },
  tokenApp({ id: 'square', name: 'Square', domain: 'connect.squareup.com', tool: 'cli', fieldLabel: "Claude's access token", routes: [{ host: 'connect.squareup.com' }], accent: '#111111' }),
  tokenApp({ id: 'stripe', name: 'Stripe', domain: 'api.stripe.com', tool: 'cli', fieldLabel: "Claude's secret key", fieldKey: 'secret_key', routes: [{ host: 'api.stripe.com' }], accent: '#635bff' }),
  tokenApp({ id: 'supabase', name: 'Supabase', domain: 'api.supabase.com', tool: 'cli', fieldLabel: "Claude's personal access token", routes: [{ host: 'api.supabase.com' }], testConnection: true, accent: '#3ecf8e' }),
  tokenApp({ id: 'tavily', name: 'Tavily', domain: 'api.tavily.com', tool: 'cli', fieldLabel: "Claude's API key", fieldKey: 'api_key', routes: [{ host: 'api.tavily.com' }], accent: '#0f766e' }),
  tokenApp({ id: 'todoist', name: 'Todoist', domain: 'api.todoist.com', tool: 'cli', fieldLabel: "Claude's API token", routes: [{ host: 'api.todoist.com' }], accent: '#e44332' }),
  tokenApp({ id: 'vercel', name: 'Vercel', domain: 'api.vercel.com', tool: 'cli', fieldLabel: "Claude's access token", routes: [{ host: 'api.vercel.com' }], accent: '#111111' }),
  tokenApp({ id: 'webflow', name: 'Webflow', domain: 'api.webflow.com', tool: 'cli', fieldLabel: "Claude's API token", routes: [{ host: 'api.webflow.com' }], accent: '#4353ff' }),
] as const;

export const curatedAppById = new Map(curatedApps.map((app) => [app.id, app]));

export function requireCuratedApp(id: string): CuratedAppDefinition {
  const definition = curatedAppById.get(id as CuratedAppId);
  if (!definition) throw new Error(`Unknown curated app: ${id}`);
  return definition;
}

export function resolveCuratedAppRoutes(
  definition: CuratedAppDefinition,
  configuration: Record<string, string>,
): CuratedAppRoute[] {
  return definition.routes.map((route) => {
    if (!route.configurable || !route.hostField) return route;
    const host = normalizeConfiguredHost(
      configuration[route.hostField] ?? '',
      route.allowedHostSuffixes,
    );
    return { ...route, host };
  });
}

export function normalizeConfiguredHost(
  value: string,
  allowedHostSuffixes: string[] = [],
): string {
  let normalized = value.trim().toLowerCase();
  if (normalized.startsWith('https://') || normalized.startsWith('http://')) {
    const url = new URL(normalized);
    if (url.pathname !== '/' || url.search || url.hash || url.username || url.password) {
      throw new Error('Allowed website must contain only a hostname.');
    }
    normalized = url.hostname;
  }
  normalized = normalized.replace(/\.$/u, '');
  if (
    !/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/u.test(normalized)
    || normalized.length > 253
  ) {
    throw new Error('Allowed website must be a valid public DNS hostname.');
  }
  if (
    allowedHostSuffixes.length > 0
    && !allowedHostSuffixes.some((suffix) =>
      suffix.startsWith('.') && normalized.endsWith(suffix) && normalized.length > suffix.length)
  ) {
    throw new Error('Allowed website is outside this provider domain.');
  }
  return normalized;
}
